import { cn } from '@/lib/utils';

export function Badge({
  className,
  tone = 'default',
  ...props
}: React.HTMLAttributes<HTMLSpanElement> & {
  tone?: 'default' | 'blue' | 'teal' | 'violet' | 'high' | 'medium' | 'low' | 'danger';
}) {
  const tones: Record<string, string> = {
    default: 'bg-bg-raised text-text-secondary border-border',
    blue: 'bg-accent-blue/10 text-accent-blue border-accent-blue/30',
    teal: 'bg-accent-teal/10 text-accent-teal border-accent-teal/30',
    violet: 'bg-accent-indigo/10 text-accent-indigo border-accent-indigo/30',
    high: 'bg-danger/10 text-danger border-danger/30',
    medium: 'bg-warning/10 text-warning border-warning/30',
    low: 'bg-accent-teal/10 text-accent-teal border-accent-teal/30',
    danger: 'bg-danger/10 text-danger border-danger/30',
  };
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium',
        tones[tone],
        className,
      )}
      {...props}
    />
  );
}

export function Spinner({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'h-5 w-5 animate-spin rounded-full border-2 border-border border-t-accent-blue',
        className,
      )}
      role="status"
      aria-label="Loading"
    />
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded-md bg-bg-raised', className)} />;
}

export function EmptyState({
  title,
  description,
  action,
  icon,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
  icon?: React.ReactNode;
}) {
  return (
    <div className="panel flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
      {icon && <div className="text-text-muted">{icon}</div>}
      <h3 className="text-lg font-semibold text-text-primary">{title}</h3>
      {description && <p className="max-w-md text-sm text-text-secondary">{description}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

export function Alert({
  children,
  tone = 'info',
}: {
  children: React.ReactNode;
  tone?: 'info' | 'error' | 'success';
}) {
  const tones = {
    info: 'border-accent-blue/30 bg-accent-blue/5 text-text-primary',
    error: 'border-danger/40 bg-danger/5 text-text-primary',
    success: 'border-accent-teal/40 bg-accent-teal/5 text-text-primary',
  };
  return (
    <div className={cn('rounded-lg border px-4 py-3 text-sm', tones[tone])} role="alert">
      {children}
    </div>
  );
}
